import { useEffect, useState } from 'react';
import type { SevenTVEmote } from '../lib/emotes.js';

// Module-level cache — shared across all component instances
let cachedEmotes: Map<string, SevenTVEmote> | null = null;
let pendingFetch: Promise<Map<string, SevenTVEmote>> | null = null;

async function load7TV(broadcasterTwitchId: string): Promise<Map<string, SevenTVEmote>> {
  const map = new Map<string, SevenTVEmote>();

  function addEmote(e: { id: string; name: string }) {
    map.set(e.name, {
      id: e.id,
      name: e.name,
      url1x: `https://cdn.7tv.app/emote/${e.id}/1x.webp`,
      url2x: `https://cdn.7tv.app/emote/${e.id}/2x.webp`,
    });
  }

  // Channel emotes
  try {
    const res = await fetch(`https://7tv.io/v3/users/twitch/${broadcasterTwitchId}`);
    if (res.ok) {
      const data = (await res.json()) as { emote_set?: { emotes?: { id: string; name: string }[] } };
      for (const e of data.emote_set?.emotes ?? []) addEmote(e);
    }
  } catch { /* network error — skip */ }

  // Global emotes
  try {
    const res = await fetch('https://7tv.io/v3/emote-sets/global');
    if (res.ok) {
      const data = (await res.json()) as { emotes?: { id: string; name: string }[] };
      for (const e of data.emotes ?? []) addEmote(e);
    }
  } catch { /* network error — skip */ }

  return map;
}

export function useEmotes(broadcasterTwitchId: string | null): Map<string, SevenTVEmote> {
  const [emotes, setEmotes] = useState<Map<string, SevenTVEmote>>(
    () => cachedEmotes ?? new Map(),
  );

  useEffect(() => {
    if (!broadcasterTwitchId) return;
    if (cachedEmotes) { setEmotes(cachedEmotes); return; }

    if (!pendingFetch) {
      pendingFetch = load7TV(broadcasterTwitchId).then((map) => {
        cachedEmotes = map;
        return map;
      });
    }

    let cancelled = false;
    pendingFetch.then((map) => { if (!cancelled) setEmotes(map); }).catch(() => {});
    return () => { cancelled = true; };
  }, [broadcasterTwitchId]);

  return emotes;
}
